import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Alert } from "rsuite";

import { DEFAULT_EQUAL_SIGN, DEFAULT_PER_PAGE_SMALL } from "const";
import { api } from "api/loginRoutes";
import { checkPermissionsFor } from "store/storeHelpers";
import { usePrevious, useWindowWidth } from "hooks";
import { convertDataInColumn, downloadNumbersTxtCommon, getServiceSmsPrefix, resolvePerPage } from "utils";
import {
    LS_ACCOUNT_ALLOCATED_NUMBERS_PER_PAGE,
    LS_ACCOUNT_ALLOCATED_NUMBERS_GAN_PER_PAGE,
    LS_ACCOUNT_ALLOCATED_NUMBERS_TRANSIT_PER_PAGE,
    LS_ALLOCATED_NUMBERS_COLUMNS_PREFIX
} from "const/localStorageKeys";

import { getPermissionFlags } from "../Config/Permissions";
import { getTableDataByMode, getTablePagingByMode } from "../Config/Data";
import {
    ALLOCATED_NUMBERS_MODE,
    getInitialDisplayByMode,
    getDownloadPlanType,
    getRowKeyByMode,
    isSupplierMode,
    NUMBER_KEY,
    RANGE_KEY,
    resolveAllocatedNumbersMode,
    SUBDEST_KEY,
} from "../Config/Mode";
import { getNumberActions, getPrefixActions } from "../Store/Actions";
import {
    allocatedNumbersModalsReducer,
    closeModal,
    consumeModalUiEffect,
    initialModalState,
    MODAL_UI_EFFECTS,
    MODALS,
    openModal,
    patchModalUi,
} from "../Store/ModalsReducer";

const defaultSelected = { all: false, list: [] };
const defaultFilter = {
    show_allocated_numbers: true,
    show_unallocated_numbers: true,
    show_test_numbers: true,
    show_block_allocation_numbers: true
};

export const useAllocatedNumbersController = ({
    trunks,
    account,
    service,
    trunk_id,
    readonly,
    permissions,
    isGanTrunks,
    raterc_list,
    trunksInited,
    account_name,
    currencyList,
    trunksLoading,
    currentSupplier,
    isTransitTrunks,
    allocatedNumbers,
    paymentTermsList,
    showReasonHandler,
    customRangesTrunk,
    suppliersGroupList,
    currentRangeNumber,
    suppliersPrefixList,
    allocatedNumbersPage,
    groupDownloadNumbers,
    allocatedNumbersCount,
    suppliersPrefixLoading,
    allocatedNumbersPerPage,
    allocatedNumbersLoading,
    suppliersPrefixListPage,
    suppliersPrefixListCount,
    showAllocatedNumbersModal,
    suppliersPrefixListPerPage,
    allocatedNumbersTotalCount,
    suppliersPrefixDropdownList,
    sp_key,
    sp_auth,
    spOtpType,
    acc_key,
    ivr_list,
    createTrafficLimit,
    modifyTrafficLimit,
    removeTrafficLimit,
    getTrunks,
    getNumbers,
    getSupplierPrefixes,
    trunkDownloadNumbers,
    getTrunkNumberCsvById,
    closeAllocatedNumbersModal,
    getSupplierPrefixesDropdown,
}) => {
    const mode = resolveAllocatedNumbersMode({ isGanTrunks, isTransitTrunks });
    const windowWidth = useWindowWidth();
    const prevTrunkId = usePrevious(trunk_id);
    const prevTrunksInited = usePrevious(trunksInited);

    const filterRef = useRef(null);
    const tableRef = useRef(null);
    const selectedListRef = useRef(new Set());

    const [modalState, modalDispatch] = useReducer(allocatedNumbersModalsReducer, initialModalState);
    const [selectedGanNumbers, setSelectedGanNumbers] = useState([]);
    const [selected, setSelected] = useState(defaultSelected);
    const [filterState, setFilterState] = useState(currentRangeNumber ? { str: currentRangeNumber || "" } : null);
    const [currentDisplay, setCurrentDisplay] = useState(getInitialDisplayByMode(mode));
    const [searchLoading, setSearchLoading] = useState(false);
    const [sort, setSort] = useState({});
    const [numberPageState, setNumberPageState] = useState(1);
    const [disabledActions, setDisabledActions] = useState([]);
    const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
    const [selectedItems, setSelectedItems] = useState(new Map());

    const isMobile = windowWidth < 1200;
    const loading = trunksLoading || allocatedNumbersLoading;
    const isSupplierTrunks = isSupplierMode(mode);
    const showToDate = filterState && Object.prototype.hasOwnProperty.call(filterState, "actual")
        ? !filterState?.actual
        : false;

    const getRowKey = () => getRowKeyByMode({ mode, currentDisplay });

    const perPageLSKey = useMemo(() => {
        if (mode === ALLOCATED_NUMBERS_MODE.gan) {
            return LS_ACCOUNT_ALLOCATED_NUMBERS_GAN_PER_PAGE;
        }

        if (mode === ALLOCATED_NUMBERS_MODE.transit) {
            return LS_ACCOUNT_ALLOCATED_NUMBERS_TRANSIT_PER_PAGE;
        }

        return LS_ACCOUNT_ALLOCATED_NUMBERS_PER_PAGE;
    }, [mode]);

    const resolvedPerPage = useMemo(() => resolvePerPage(
        perPageLSKey,
        null,
        DEFAULT_PER_PAGE_SMALL,
    ), [perPageLSKey]);

    const [currentPerPage, setCurrentPerPage] = useState(resolvedPerPage);

    const columnSelectorMode = useMemo(() => {
        if (mode === ALLOCATED_NUMBERS_MODE.transit) {
            return "transit";
        }

        if (mode === ALLOCATED_NUMBERS_MODE.gan) {
            return "gan";
        }

        return "allocated";
    }, [mode]);

    const columnSelectorService = service ? "voice" : "sms";
    const columnSelectorLSKey = `${LS_ALLOCATED_NUMBERS_COLUMNS_PREFIX}${columnSelectorMode}_${columnSelectorService}_${currentDisplay}`;

    useEffect(() => {
        setCurrentPerPage(resolvedPerPage);
    }, [resolvedPerPage]);

    useEffect(() => {
        const nextDisplay = getInitialDisplayByMode(mode);

        if (currentDisplay !== nextDisplay) {
            if (tableRef.current) {
                tableRef.current.clear();
            }

            selectedListRef.current.clear();
            clearSelectedNumbers();
            setSelected(defaultSelected);
            setCurrentDisplay(nextDisplay);
        }
    }, [mode]);

    const columnsByMode = useMemo(() => ({
        mode,
        showToDate,
    }), [mode, showToDate]);

    useEffect(() => {
        if (currentDisplay === "numbers" && filterState === null) {
            setFilterState({
                ...defaultFilter,
                ...(currentRangeNumber ? { str: currentRangeNumber } : {}),
            });
        }

        const filters = {
            start_date_inequation: 0,
            ...defaultFilter,
            ...(currentRangeNumber ? { str: currentRangeNumber } : {}),
            ...getCurrentFilters()
        };

        const becameInited = !prevTrunksInited && trunksInited;
        const trunkChanged = trunk_id !== prevTrunkId;

        if (trunks.length && trunk_id && trunksInited && (becameInited || trunkChanged)) {
            getNumbers(currentDisplay, filters, 1, currentPerPage);
        }

        if (tableRef.current) {
            tableRef.current.clear();
        }
        selectedListRef.current.clear();
        setSelectedItems(new Map());
    }, [trunk_id, trunksInited]);

    useEffect(() => {
        if (!currentRangeNumber) return;

        const currentFilterState = filterState || {};
        onChangeFilters({
            ...currentFilterState,
            str: currentRangeNumber,
        });
    }, [currentRangeNumber]);

    const clearSelectedNumbers = () => {
        setSelectedItems(new Map());
    };

    const resetFooterActions = () => {
        modalDispatch(patchModalUi({
            footerActionsDisabled: false,
            footerActionsLoading: false,
        }));
    };

    const closeModalWithUiReset = (modalKey, payload = null) => {
        modalDispatch(closeModal(modalKey, payload));
    };

    useEffect(() => {
        if (!modalState.uiEffects.length) {
            return;
        }

        const currentEffect = modalState.uiEffects[0];

        switch (currentEffect) {
            case MODAL_UI_EFFECTS.resetFooterActions:
                resetFooterActions();
                break;
            case MODAL_UI_EFFECTS.clearTableSelection:
                if (tableRef.current) {
                    tableRef.current.clear();
                }
                selectedListRef.current.clear();
                clearSelectedNumbers();
                break;
            default:
                break;
        }

        modalDispatch(consumeModalUiEffect());
    }, [modalState.uiEffects]);

    const setFocusOnInput = (ref) => {
        if (
            ref.current &&
            Object.keys(ref.current).includes("_reactInternalFiber") &&
            Object.keys(ref.current._reactInternalFiber).includes("firstEffect") &&
            !!ref.current._reactInternalFiber.firstEffect &&
            ref.current._reactInternalFiber.firstEffect.stateNode &&
            ref.current._reactInternalFiber.firstEffect.stateNode.hasAttribute &&
            ref.current._reactInternalFiber.firstEffect.stateNode.hasAttribute("focus")
        ) {
            ref.current._reactInternalFiber.firstEffect.stateNode.focus();
        }
    };

    const getCurrentFilters = () => {
        if (filterState) {
            return filterState;
        }

        if (filterRef.current && filterRef.current.getValues) {
            return filterRef.current.getValues();
        }

        return {
            ...defaultFilter,
            ...(currentRangeNumber ? { str: currentRangeNumber } : {}),
        };
    };

    const getNumberList = (page, per_page = currentPerPage, nextSort) => {
        if (per_page !== currentPerPage) {
            setCurrentPerPage(per_page);
        }

        getNumbers(currentDisplay, getCurrentFilters(), page, per_page, nextSort);
    };

    const onChangeFilters = (filters) => {
        if (!searchLoading) setSearchLoading(true);

        const filterStateObject = filterState || {};

        const existFilters = Object.keys(filterStateObject).reduce((result, value) => {
            if (!(value in defaultFilter)) {
                if (value in filters) result[value] = filterStateObject[value];
            } else if (value in defaultFilter) {
                if (filterStateObject[value] !== defaultFilter[value]) result[value] = filterStateObject[value];
            }

            return result;
        }, defaultFilter);

        const filterObject = {
            ...existFilters,
            ...filters,
            start_date_inequation: filters.start_date_inequation
                ? Number.parseInt(filters.start_date_inequation)
                : DEFAULT_EQUAL_SIGN
        };

        Object.keys(filterObject).forEach((key) => {
            if (filterObject[key] === undefined || filterObject[key] === null) {
                delete filterObject[key];
            }
        });

        setTimeout(() => {
            setSearchLoading(false);
            setFilterState(filterObject);
            setFocusOnInput(filterRef);
        }, 1000);

        getNumbers(currentDisplay, filterObject, 1, currentPerPage, sort);
    };

    const onChangeGroup = (nextDisplay) => {
        if (tableRef.current) {
            tableRef.current.clear();
        }
        selectedListRef.current.clear();
        clearSelectedNumbers();
        setCurrentDisplay(nextDisplay);

        if (["numbers", "groups"].includes(nextDisplay)) {
            setSelected(defaultSelected);
        }

        getNumbers(nextDisplay, getCurrentFilters(), 1, currentPerPage, sort);
    };

    const getInitialValues = () => {
        if (selected.all) {
            return { filter: getCurrentFilters(), filterAll: true };
        }

        switch (currentDisplay) {
            case "trunk_number":
                return { trn_key_list: selected.list };
            case "price_range":
                return { pr_key_list: selected.list };
            case "subdestination":
                return { sde_key_list: selected.list };
            default:
                return {};
        }
    };

    const updateItems = () => {
        if (tableRef.current) {
            tableRef.current.clear();
        }
        selectedListRef.current.clear();
        clearSelectedNumbers();

        getTrunks();
        getNumbers(currentDisplay, getCurrentFilters(), allocatedNumbersPage, currentPerPage, sort);
    };

    const proxyFilters = (filters) => ({
        ...filters,
        actual: !filters.actual
    });

    const updateSelectedList = (nextSelected) => {
        const data = isSupplierTrunks ? suppliersPrefixList || [] : allocatedNumbers || [];
        const rowKey = getRowKey();

        for (const item of selectedListRef.current) {
            if (!nextSelected.list.find((key) => key === item[rowKey])) {
                selectedListRef.current.delete(item);
            }
        }

        nextSelected.list.forEach((key) => {
            const value = data.find((item) => item[rowKey] === key);
            if (value) selectedListRef.current.add(value);
        });

        if (nextSelected.list.length === 0) {
            clearSelectedNumbers();
        }

        if (isSupplierTrunks) {
            const selectedValues = Array.from(selectedListRef.current.values()).map((value) => value?.trafic_limit_list || []);
            const firstTrafficLimitValue = selectedValues[0];
            const trafficsAllTheSame = firstTrafficLimitValue
                ? selectedValues.every((trafficList) => {
                    if (firstTrafficLimitValue.length === trafficList.length) {
                        return trafficList.every((value, idx) => {
                            if (firstTrafficLimitValue[idx]) {
                                return value.day === firstTrafficLimitValue[idx].day &&
                                    value.hour === firstTrafficLimitValue[idx].hour;
                            }
                            return true;
                        });
                    }
                    return false;
                })
                : false;

            if (!trafficsAllTheSame) {
                setDisabledActions(["Set limit", ...disabledActions]);
            } else {
                setDisabledActions(disabledActions.filter((action) => action !== "Set limit"));
            }
        }

        if (isGanTrunks) {
            const filteredNumbers = selectedGanNumbers.filter((item) => nextSelected.list.includes(item.inn_key));
            const currentPageNumbers = data.filter((item) => nextSelected.list.includes(item.inn_key));
            const numbersUpdated = currentPageNumbers.reduce((summ, item) => {
                if (summ.find((_item) => _item.inn_key === item.inn_key)) {
                    return summ;
                }
                return [...summ, item];
            }, [...filteredNumbers]);

            setSelectedGanNumbers([...numbersUpdated]);
        }

        setSelected(nextSelected);
    };

    const getDataForChangeRate = () => {
        for (const item of selectedListRef.current) {
            return {
                sde_key: item.sde_key,
                sde_name: item.subdestination_name,
                account_name,
                sp_key
            };
        }

        return undefined;
    };

    const getDataForChangeSupplierRate = () => {
        const data = [];

        for (const item of selectedListRef.current) {
            data.push({
                sde_key: item.sde_key,
                de_key: item.de_key,
                key: item.key,
                rate: item.rate,
                account_name
            });
        }

        return data;
    };

    const handleDownloadNumbersCsv = async () => {
        const plan_type = getDownloadPlanType(mode);

        setIsDownloadInProgress(true);
        await trunkDownloadNumbers(trunk_id, service, plan_type);
        setIsDownloadInProgress(false);
    };

    const handleDownloadOnlyNumbers = () => {
        const filter = getCurrentFilters();
        const plan_type = getDownloadPlanType(mode);
        const fileNames = {
            transit: "transit_prefixes.txt",
            gan: "gan_prefixes.txt",
            default: "allocated_numbers.txt"
        };

        setIsDownloadInProgress(true);

        downloadNumbersTxtCommon(
            trunk_id,
            filter,
            () => setIsDownloadInProgress(false),
            service,
            (fileNames[plan_type] || fileNames.default),
            plan_type,
            isSupplierMode(mode)
        );
    };

    const keepSelectedItems = (data, checked) => {
        const updatedCollection = new Map(selectedItems);
        const rowKey = getRowKey();

        for (const item of data) {
            if (checked) {
                updatedCollection.set(item[rowKey], item);
            } else {
                updatedCollection.delete(item[rowKey]);
            }
        }

        setSelectedItems(updatedCollection);
    };

    const getSelectedNumbersCount = () => {
        const { list, all } = selected;

        if (all) return allocatedNumbersTotalCount;

        const rowKey = getRowKey();

        let key = "id";
        if (!isTransitTrunks && !isGanTrunks) {
            switch (rowKey) {
                case NUMBER_KEY:
                    key = "numbers_count";
                    break;
                case RANGE_KEY:
                    key = "price_range_trunk_numbers";
                    break;
                case SUBDEST_KEY:
                    key = "subdestination_trunk_numbers";
                    break;
                default:
                    key = "id";
            }

            let count = 0;
            for (const item of selectedItems) {
                count += item[1][key];
            }

            return count;
        }

        return list.length;
    };

    const handleOpenSetActiveModal = (status) => {
        modalDispatch(openModal(MODALS.setActive, { activeStatus: status }));
    };

    const handleOpenDeletePrefixesModal = () => {
        modalDispatch(openModal(MODALS.deletePrefixes));
    };

    const handleOpenChangeRateModal = () => {
        modalDispatch(patchModalUi({
            footerActionsDisabled: true,
            footerActionsLoading: true,
        }));

        const initialData = getInitialValues();
        const targetTrunkByService = { [getServiceSmsPrefix(service, "trunk_id")]: trunk_id };
        const getSubdestMethod = getServiceSmsPrefix(service, "trunk_number__filter:get_subdestination_list");

        api(getSubdestMethod, { target: targetTrunkByService, ...initialData })
            .then((response) => {
                if (!response || !response.subdestination_list) return;

                const isUniqueDest = response.subdestination_list
                    .map((item) => item.de_key)
                    .every((val, index, arr) => val === arr[0]);

                if (!isUniqueDest) {
                    Alert.error("You cannot change the rate for different destinations at one time");
                    return;
                }

                modalDispatch(openModal(MODALS.changeRate, { currentSubDestinationsList: response.subdestination_list }));
            })
            .finally(() => {
                resetFooterActions();
            });
    };

    const handleOpenChangeSupplierRateModal = () => {
        const data = getDataForChangeSupplierRate();

        const isUniqueSubdest = data
            .map((value) => value.de_key)
            .every((val, index, arr) => val === arr[0]);

        if (isUniqueSubdest) {
            modalDispatch(openModal(MODALS.changeSupplierRate));
        } else {
            Alert.error("You cannot change the rate for different destinations at one time");
        }
    };

    const handleGroupAction = (modalKey, rowData) => {
        modalDispatch(openModal(modalKey, { selectedGroupData: { ...rowData } }));
    };

    const ganNumberSuccess = () => {
        getTrunks(true);
        updateItems();
    };

    const modalHostProps = {
        opened: modalState.opened,
        payload: modalState.payload,
        closeModal: closeModalWithUiReset,
        openModal: (key, payload) => modalDispatch(openModal(key, payload)),
        service,
        responsiveTarget: { [getServiceSmsPrefix(service, "trunk_id")]: trunk_id },
        acc_key,
        getInitialValues,
        sp_key,
        account_name,
        updateItems,
        getTrunks,
        getDataForChangeRate,
        trunk_id,
        selectedItems,
        getDataForChangeSupplierRate,
        getSelectedNumbersCount,
        trunks,
        currentTrunk: trunks.find((trunk) => trunk.id === trunk_id),
        raterc_list,
        ivr_list,
        allocatedNumbers,
        suppliersPrefixList,
        createTrafficLimit,
        modifyTrafficLimit,
        removeTrafficLimit,
        currentSupplier,
        getSupplierPrefixes,
        currentDisplay,
        currencyList,
        paymentTermsList,
        showReasonHandler,
    };

    const tableData = getTableDataByMode({
        mode,
        currentDisplay,
        numbersData: convertDataInColumn(
            allocatedNumbers && allocatedNumbers.length ? allocatedNumbers.map((item) => ({
                ...item,
                start_date: item.start_date ? item.start_date.replace(" ", "T") : "",
                end_date: item.end_date ? item.end_date.replace(" ", "T") : "",
            })) : [],
            "end_date",
            (value, column, key) => ({
                ...column,
                [key]: value
                    ? new Date(new Date(new Date(value).setSeconds(new Date(value).getSeconds() - 1)))
                    : null
            })
        ),
        suppliersPrefixList,
        suppliersGroupList,
    });

    const paging = {
        ...getTablePagingByMode({
            isSupplier: isSupplierTrunks,
            allocatedNumbersCount,
            allocatedNumbersPage,
            allocatedNumbersPerPage,
            suppliersPrefixListCount,
            suppliersPrefixListPage,
            suppliersPrefixListPerPage,
        }),
        dataPerPage: currentPerPage,
    };

    const permissionFlags = useMemo(() => getPermissionFlags({ service, checkPermissionsFor, permissions }), [service, checkPermissionsFor, permissions]);

    const numberActions = getNumberActions({
        showChangeRateNumbersButton: permissionFlags.showChangeRateNumbersButton,
        showRevokeNumbersButton: permissionFlags.showRevokeNumbersButton,
        showMoveToOtherTrunkButton: permissionFlags.showMoveToOtherTrunkButton,
        service,
        onOpenChangeRate: handleOpenChangeRateModal,
        onOpenRevoke: () => modalDispatch(openModal(MODALS.revoke)),
        onOpenMove: () => modalDispatch(openModal(MODALS.move)),
        onOpenChangeBillingType: () => modalDispatch(openModal(MODALS.changeBT)),
        onOpenSetIvr: () => modalDispatch(openModal(MODALS.ivr)),
    });
    const prefixActions = getPrefixActions({
        showModifyPrefixesButton: permissionFlags.showModifyPrefixesButton,
        showActivePrefixesButton: permissionFlags.showActivePrefixesButton,
        showDeletePrefixesButton: permissionFlags.showDeletePrefixesButton,
        onOpenChangeSupplierRate: handleOpenChangeSupplierRateModal,
        onActivate: () => handleOpenSetActiveModal(true),
        onDeactivate: () => handleOpenSetActiveModal(false),
        onDeletePrefixes: handleOpenDeletePrefixesModal,
    });

    const selectedGan = Array.from(selectedItems.values());
    const hasGanBlocked = selectedGan.length && selectedGan.some((number) => number.blocked);
    const hasSelected = (selected.list && selected.list.length) || selected.all;
    const ganNumbersExtraParams = {
        target: {
            "sms.supplier_trunk_id": trunk_id
        },
        "sms.supplier_gan_key_list": !selected.all ? selected.list : undefined
    };
    const dataLoading = searchLoading || (isSupplierTrunks ? suppliersPrefixLoading : loading);

    return {
        mode,
        filterRef,
        tableRef,
        selectedListRef,
        modalState,
        modalDispatch,
        footerActionsDisabled: modalState.ui.footerActionsDisabled,
        footerActionsLoading: modalState.ui.footerActionsLoading,
        selectedGanNumbers,
        selected,
        setSelected,
        filterState,
        setFilterState,
        currentDisplay,
        setCurrentDisplay,
        searchLoading,
        setSearchLoading,
        sort,
        setSort,
        numberPageState,
        setNumberPageState,
        currentPerPage,
        setCurrentPerPage,
        perPageLSKey,
        disabledActions,
        setDisabledActions,
        isDownloadInProgress,
        setIsDownloadInProgress,
        selectedItems,
        setSelectedItems,
        isMobile,
        loading,
        columnsByMode,
        getRowKey,
        onChangeFilters,
        onChangeGroup,
        getInitialValues,
        updateItems,
        proxyFilters,
        updateSelectedList,
        getDataForChangeRate,
        getDataForChangeSupplierRate,
        handleDownloadNumbersCsv,
        handleDownloadOnlyNumbers,
        keepSelectedItems,
        getSelectedNumbersCount,
        handleOpenSetActiveModal,
        handleOpenDeletePrefixesModal,
        handleOpenChangeRateModal,
        handleOpenChangeSupplierRateModal,
        handleGroupAction,
        ganNumberSuccess,
        modalHostProps,
        tableData,
        paging,
        hasGanBlocked,
        hasSelected,
        ganNumbersExtraParams,
        permissionFlags,
        numberActions,
        prefixActions,
        currentActions: isTransitTrunks ? prefixActions : numberActions,
        responsiveTarget: { [getServiceSmsPrefix(service, "trunk_id")]: trunk_id },
        isSupplierTrunks,
        showToDate,
        getNumberList,
        resetFooterActions,
        closeModalWithUiReset,
        dataLoading,
        columnSelectorLSKey,
        getDownloadPlanType,
    };
};
